import * as React from 'react'
import { skipToken, useQuery, type UseQueryResult } from '@tanstack/react-query'
import type { ApiRequestDetailDto, ApiRequestListDto } from '@lib/dto/request-log.dto'
import type { ApiRequestQuery } from '@lib/input/request-log.input'
import { useDashboardScope } from '@/dashboard/DashboardScope'
import { requestLogKeys } from '@/hooks/queryKeys'
import { keepPreviousWithin } from '@/hooks/keepPreviousWithin'
import type { Uuid } from '@lib/validators/params'
import { RequestLogService } from '@/services/RequestLogService'

function useScopedService(): RequestLogService {
  const { client } = useDashboardScope()
  return React.useMemo(() => new RequestLogService(client), [client])
}

export function useApiRequests(query: ApiRequestQuery): UseQueryResult<ApiRequestListDto> {
  const { tenantId, cacheKey, mode } = useDashboardScope()
  const service = useScopedService()
  return useQuery({
    queryKey: requestLogKeys.list(tenantId, cacheKey, mode, query),
    queryFn: () => service.list(tenantId, mode, query),
    placeholderData: keepPreviousWithin(requestLogKeys.scope(tenantId, cacheKey, mode)),
  })
}

export function useApiRequest(id: Uuid | null): UseQueryResult<ApiRequestDetailDto> {
  const { tenantId, cacheKey, mode } = useDashboardScope()
  const service = useScopedService()
  return useQuery({
    queryKey: requestLogKeys.detail(tenantId, cacheKey, mode, id),
    queryFn: id !== null ? () => service.detail(tenantId, mode, id) : skipToken,
  })
}
